import { ArticleAction } from '../types';
import { i18n } from '@/plugins/i18n';
import { platform, AllPlatforms } from '@/common/bridge';
import { store } from '@/store';
import { UserState } from '@/store/user';
import { CompiledPlatformSupport, getVersionSupportCheckers } from './VersionCheck.static';

/**
 * These actions take to a specific page/screen/form
 * in application.
 */
export const PLEASED_ARTICLE_ACTIONS = {
  GO_DEPOSIT: 'GO_DEPOSIT',
  GO_WITHDRAW: 'GO_WITHDRAW',
  GO_BET_LIVE: 'GO_BET_LIVE',
  GO_BET_FOOTBALL_LIVE: 'GO_BET_FOOTBALL_LIVE',
  GO_TRANSACTIONS: 'GO_TRANSACTIONS',

  // To be supported
  GO_ACCOUNT_SETTINGS: 'GO_ACCOUNT_SETTINGS',
  GO_PROFILE_PAGE: 'GO_PROFILE_PAGE',
  GO_REGISTER: 'GO_REGISTER',
  GO_SPORTY_PIN: 'GO_SPORTY_PIN',
  GO_NAME_UPDATE: 'GO_NAME_UPDATE',
  GO_KYC_VERIFICATION: 'GO_KYC_VERIFICATION',
} as const;

export type PleasedArticleAction = keyof typeof PLEASED_ARTICLE_ACTIONS;

export const PLEASED_ARTICLE_ACTIONS_V1_LIST: PleasedArticleAction[] = [
  PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT,
  PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW,
  PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE,
  PLEASED_ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE,
  PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS,
];

export const isFirstVersionArticleAction = (action: PleasedArticleAction): boolean => {
  return PLEASED_ARTICLE_ACTIONS_V1_LIST.includes(action);
};

// iOS/Android deep link to pass page as argument
export const PLEASED_ARTICLE_APP_ACTION = 'GO_DEEP_LINK';
export const DEEP_LINK_URL_POSTFIX = 'applink';
export const SHORTCUT_ARTICLE_ACTIONS: PleasedArticleAction[] = [PLEASED_ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE];

export const PLEASED_ARTICLE_DEEP_LINKS: {
  [key in PleasedArticleAction]: string;
} = {
  [PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT]: 'deposit',
  [PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW]: 'withdraw',
  [PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE]: 'live',
  [PLEASED_ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE]: 'live?key_sport_id=sr:sport:1',
  [PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS]: 'trans',
  [PLEASED_ARTICLE_ACTIONS.GO_ACCOUNT_SETTINGS]: 'account_settings',
  [PLEASED_ARTICLE_ACTIONS.GO_PROFILE_PAGE]: 'userinfo',
  [PLEASED_ARTICLE_ACTIONS.GO_REGISTER]: 'register',
  [PLEASED_ARTICLE_ACTIONS.GO_SPORTY_PIN]: 'multifactor_authentication',
  [PLEASED_ARTICLE_ACTIONS.GO_NAME_UPDATE]: 'name_update',
  [PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION]: 'kyc',
};
interface VersionSupport {
  supports?: CompiledPlatformSupport
}

const make = (x: ArticleAction & VersionSupport) => ({ ...x,
  supports: {
    ...getVersionSupportCheckers('articleActions'),
    ...x.supports,
  } as VersionSupport['supports'] } as ArticleAction & VersionSupport);

const makeV2 = (x: ArticleAction & VersionSupport) => ({ ...x,
  supports: {
    ...getVersionSupportCheckers('articleActionsV2', false),
    ...x.supports,
  } as VersionSupport['supports'] } as ArticleAction & VersionSupport);

const t = (key: string) => i18n.t(key) as string;

const ARTICLE_ACTIONS = {
  [PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT,
    actionMessage: t('actions.go_deposit.action_message') as string,
    actionTitle: t('actions.go_deposit.action_title') as string,
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW,
    actionMessage: t('actions.go_withdrawal.action_message'),
    actionTitle: t('actions.go_withdrawal.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE,
    actionMessage: t('actions.go_bet_live.action_message'),
    actionTitle: t('actions.go_bet_live.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE,
    actionMessage: t('actions.go_bet_football_live.action_message'),
    actionTitle: t('actions.go_bet_live.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_ACCOUNT_SETTINGS]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_ACCOUNT_SETTINGS,
    actionMessage: t('actions.go_account_settings.action_message'),
    actionTitle: t('actions.go_account_settings.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS,
    actionMessage: t('actions.go_transactions.action_message'),
    actionTitle: t('actions.go_transactions.action_message'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION]: () => makeV2({
    action: PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION,
    actionMessage: t('actions.go_kyc_verification.action_message'),
    actionTitle: t('actions.go_kyc_verification.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_NAME_UPDATE]: () => makeV2({
    action: PLEASED_ARTICLE_ACTIONS.GO_NAME_UPDATE,
    actionMessage: t('actions.go_name_update.action_message'),
    actionTitle: t('actions.go_name_update.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_PROFILE_PAGE]: () => makeV2({
    action: PLEASED_ARTICLE_ACTIONS.GO_PROFILE_PAGE,
    actionMessage: t('actions.go_profile_page.action_message'),
    actionTitle: t('actions.go_profile_page.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_REGISTER]: () => makeV2({
    action: PLEASED_ARTICLE_ACTIONS.GO_REGISTER,
    actionMessage: t('actions.go_register.action_message'),
    actionTitle: t('actions.go_register.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_SPORTY_PIN]: () => makeV2({
    action: PLEASED_ARTICLE_ACTIONS.GO_SPORTY_PIN,
    actionMessage: t('actions.go_sporty_pin.action_message'),
    actionTitle: t('actions.go_sporty_pin.action_title'),
  }),
};

export const StaticArticleActions = {
  ...ARTICLE_ACTIONS,
  DEPOSIT: ARTICLE_ACTIONS.GO_DEPOSIT,
  GO_TO_DEPOSIT: ARTICLE_ACTIONS.GO_DEPOSIT,
  GO_TO_WITHDRAW: ARTICLE_ACTIONS.GO_WITHDRAW,
  PLACE_FOOTBALL_BET: ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE,
  PLACE_A_FOOTBALL_BET: ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE,
  PLACE_A_BET: ARTICLE_ACTIONS.GO_BET_LIVE,
  PLACE_BET: ARTICLE_ACTIONS.GO_BET_LIVE,
  GO_TO_KYC: ARTICLE_ACTIONS.GO_KYC_VERIFICATION,
};

export function resolveArticleAction (action: Partial<ArticleAction>): ArticleAction {
  const key = action?.action! as keyof typeof StaticArticleActions;
  if (!StaticArticleActions[key]) return action as ArticleAction;
  const staticAction = StaticArticleActions[key]();
  return {
    action: (action.action || staticAction.action),
    actionMessage: action.actionMessage || staticAction.actionMessage,
    actionTitle: action.actionTitle || staticAction.actionTitle,
  };
}

export function checkActionSupported (action?: Partial<ArticleAction> | null): boolean {
  const key = action?.action! as keyof typeof StaticArticleActions;
  if (!StaticArticleActions[key]) {
    // This is a new action, we will check the default support for this action
    // By default, new action will only be supported on WEB/WAP, to avoid showing incorrect
    // actions that might not be supported by the platform.
    return platform === 'web' || platform === 'wap';
  }
  const staticAction = StaticArticleActions[key]();
  // If the action is defined, check platform.
  const vuexState = store.state as { user: UserState };
  // If we are not able to find the platform, it's safer to disable the action
  return Boolean(staticAction.supports?.[platform as AllPlatforms]?.(vuexState.user.customerAppVersion || ''));
}
